/**
 * Real-Time WebSocket Notification System
 * 
 * Eliminates all polling by using WebSocket push notifications for:
 * - Unread message counts
 * - New messages
 * - Message read receipts
 * - Conversation status changes
 * - Agent presence updates
 * 
 * @version 1.0.0
 * @author CleverAct AI Team - Real-Time Excellence
 */

import { logger } from '../utils/logger'
import { getValidToken } from '../utils/tokenUtils'
import { API_BASE_URL } from '../utils/constants'

export interface UnreadCountUpdate {
  type: 'unread_count_update'
  conversation_id: string
  unread_count: number
  total_unread: number
  timestamp: string
}

export interface NewMessageNotification {
  type: 'new_message'
  conversation_id: string
  message_id: string
  sender_type: 'user' | 'agent'
  content: string
  timestamp: string
}

export interface MessageReadNotification {
  type: 'messages_read'
  conversation_id: string
  reader_user_id: string
  message_ids: string[]
  read_at: string
}

export interface ConversationUpdate {
  type: 'conversation_update'
  conversation_id: string
  status?: string
  priority?: string
  assigned_agent_id?: string
  assigned_agent_name?: string
  last_message?: string
  last_message_time?: string
}

export interface AgentPresenceUpdate {
  type: 'agent_presence_update'
  agent_id: string
  is_online: boolean
  last_seen?: string
}

export interface NewConversationAssignment {
  type: 'new_conversation_assigned'
  conversation_id: string
  conversation_data: {
    conversation_id: string
    chatbot_id?: string
    visitor_name?: string
    handoff_reason?: string
    context_summary?: string
    assigned_agent_name?: string
    status: string
    created_at?: string
  }
  timestamp: string
}

export interface ConversationHandoff {
  type: 'conversation_handoff'
  conversation_id: string
  from_type: 'ai' | 'agent'
  handoff_data: {
    handoff_reason?: string
    context_summary?: string
    assigned_agent_name?: string
  }
  timestamp: string
}

export interface MessageReceived {
  type: 'message_received'
  conversation_id: string
  content?: string
  source?: string
  data?: {
    content?: string
    message?: string
    role?: string
    source?: string
  }
  timestamp: string
}

export interface WidgetMessageReceived {
  type: 'widget_message_received'
  conversation_id: string
  message_id?: string
  message_content?: string
  role?: string
  unread_count?: number
  timestamp: string
}

export type RealTimeNotification = 
  | UnreadCountUpdate 
  | NewMessageNotification 
  | MessageReadNotification 
  | ConversationUpdate
  | AgentPresenceUpdate
  | NewConversationAssignment
  | ConversationHandoff
  | MessageReceived
  | WidgetMessageReceived

export interface RealTimeEventHandlers {
  onUnreadCountUpdate?: (data: UnreadCountUpdate) => void
  onNewMessage?: (data: NewMessageNotification) => void
  onMessageRead?: (data: MessageReadNotification) => void
  onConversationUpdate?: (data: ConversationUpdate) => void
  onAgentPresenceUpdate?: (data: AgentPresenceUpdate) => void
  onNewConversationAssigned?: (data: NewConversationAssignment) => void
  onConversationHandoff?: (data: ConversationHandoff) => void
  onMessageReceived?: (data: MessageReceived) => void
  onWidgetMessageReceived?: (data: WidgetMessageReceived) => void
  onConnectionChange?: (connected: boolean) => void
  onError?: (error: string) => void
}

class RealTimeNotificationService {
  private ws: WebSocket | null = null
  private reconnectTimer: NodeJS.Timeout | null = null
  private isConnecting = false
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 1000 // Start with 1 second
  private listeners = new Map<string, Set<(data: any) => void>>()
  private eventHandlers: RealTimeEventHandlers = {}
  private authToken: string | null = null

  /**
   * Initialize the real-time notification service
   */
  async connect(token?: string): Promise<boolean> {
    if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN)) {
      return true
    }

    this.isConnecting = true
    this.authToken = token || getValidToken()

    if (!this.authToken) {
      logger.debug('No valid auth token available for WebSocket connection - will retry')
      this.isConnecting = false
      
      // Retry after a short delay in case auth is still loading
      setTimeout(() => {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
          logger.debug('🔄 Retrying WebSocket connection after auth delay')
          this.connect()
        }
      }, 500)
      
      return false
    }

    try {
      // Use the correct WebSocket endpoint for real-time notifications
      const baseUrl = API_BASE_URL
      const wsUrl = `${baseUrl.replace('https://', 'wss://').replace('http://', 'ws://')}/ws/notifications?token=${this.authToken}`
      
      logger.info('🔌 Connecting to real-time notifications...', { 
        wsUrl: wsUrl.replace(this.authToken, 'TOKEN_HIDDEN'),
        hasToken: !!this.authToken,
        tokenLength: this.authToken?.length || 0
      })
      
      this.ws = new WebSocket(wsUrl)
      
      this.ws.onopen = () => {
        logger.info('✅ Real-time notifications connected!')
        console.log('🔌 WebSocket connection opened successfully for notifications!')
        this.isConnecting = false
        this.reconnectAttempts = 0
        this.reconnectDelay = 1000
        
        // Send subscription messages
        this.subscribeToNotifications()
        
        // Notify listeners
        this.eventHandlers.onConnectionChange?.(true)
      }

      this.ws.onmessage = (event) => {
        try {
          const notification: RealTimeNotification = JSON.parse(event.data)
          
          // Enhanced logging for debugging
          console.log('🔍 RAW WebSocket message received:', {
            type: notification.type,
            data: event.data,
            timestamp: new Date().toISOString()
          })
          
          this.handleNotification(notification)
        } catch (error) {
          logger.error('Failed to parse WebSocket message', { error, data: event.data })
        }
      }

      this.ws.onclose = (event) => {
        logger.warn('🔌 Real-time notifications disconnected', { 
          code: event.code, 
          reason: event.reason,
          wasClean: event.wasClean,
          timestamp: new Date().toISOString()
        })
        
        this.ws = null
        this.isConnecting = false
        this.eventHandlers.onConnectionChange?.(false)
        
        // Auto-reconnect only if not a clean close
        if (event.code !== 1000) {
          this.scheduleReconnect()
        }
      }

      this.ws.onerror = (error) => {
        logger.error('❌ WebSocket error', { 
          error,
          readyState: this.ws?.readyState,
          url: this.ws?.url,
          timestamp: new Date().toISOString()
        })
        this.eventHandlers.onError?.('WebSocket connection error')
      }

      return true

    } catch (error) {
      logger.error('❌ Failed to connect to real-time notifications', { error })
      this.isConnecting = false
      this.eventHandlers.onError?.('Failed to establish connection')
      return false
    }
  }

  /**
   * Disconnect from real-time notifications
   */
  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    if (this.ws) {
      this.ws.close()
      this.ws = null
    }

    this.isConnecting = false
    this.eventHandlers.onConnectionChange?.(false)
    logger.info('🔌 Real-time notifications disconnected')
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      logger.error('❌ Max reconnection attempts reached')
      this.eventHandlers.onError?.('Connection failed after multiple attempts')
      return
    }

    this.reconnectAttempts++
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1)

    logger.info(`🔄 Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`)

    this.reconnectTimer = setTimeout(() => {
      this.connect(this.authToken || undefined)
    }, delay)
  }

  /**
   * Subscribe to notification types
   */
  private subscribeToNotifications(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return

    // Subscribe to unread count updates
    this.send({
      type: 'subscribe',
      channels: ['unread_counts', 'new_messages', 'message_read', 'conversation_updates', 'agent_presence']
    })
  }

  /**
   * Send message through WebSocket
   */
  private send(data: any): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      logger.warn('Cannot send message - WebSocket not connected')
      return
    }

    try {
      this.ws.send(JSON.stringify(data))
    } catch (error) {
      logger.error('Failed to send WebSocket message', { error, data })
    }
  }

  /**
   * Handle incoming notifications
   */
  private handleNotification(notification: RealTimeNotification): void {
    logger.debug('📨 Received real-time notification', { type: notification.type, notification })

    // Call specific handlers
    switch (notification.type) {
      case 'unread_count_update':
        this.eventHandlers.onUnreadCountUpdate?.(notification)
        this.notifyListeners('unread_count_update', notification)
        
        // 🔵 Dispatch unread count update as custom event for sidebar
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('unreadCountUpdate', {
            detail: {
              conversationId: notification.conversation_id,
              unreadCount: notification.unread_count,
              timestamp: notification.timestamp
            }
          }))
        }
        break
      
      case 'new_message':
        this.eventHandlers.onNewMessage?.(notification)
        this.notifyListeners('new_message', notification)
        break
      
      case 'messages_read':
        this.eventHandlers.onMessageRead?.(notification)
        this.notifyListeners('messages_read', notification)
        break
      
      case 'conversation_update':
        // 🐛 DEBUG: Special logging for resolved status
        const updateNotification = notification as ConversationUpdate
        if (updateNotification.status === 'resolved') {
          console.warn('🐛 DEBUG: WebSocket RESOLVED notification processing!', {
            type: notification.type,
            conversation_id: updateNotification.conversation_id,
            status: updateNotification.status,
            resolved_at: (notification as any).resolved_at,
            resolved_by: (notification as any).resolved_by,
            timestamp: notification.timestamp
          })
        }
        
        logger.info('🔄 Conversation update notification received', {
          type: notification.type,
          conversation_id: updateNotification.conversation_id,
          status: updateNotification.status,
          assigned_agent_id: updateNotification.assigned_agent_id,
          assigned_agent_name: updateNotification.assigned_agent_name,
          priority: (notification as any).priority,
          timestamp: new Date().toISOString()
        })
        this.eventHandlers.onConversationUpdate?.(notification)
        this.notifyListeners('conversation_update', notification)
        
        // 🎯 Dispatch custom DOM event for components listening to conversation updates
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('conversation_update', {
            detail: {
              conversation_id: (notification as ConversationUpdate).conversation_id,
              type: 'conversation_update',
              ...(notification as any), // Include all update data (priority, status, etc.)
              timestamp: notification.timestamp
            }
          }))
          
          // 🎯 Also dispatch assignment_change event if assignment-related fields changed
          const hasAssignmentChange = updateNotification.assigned_agent_id !== undefined || 
                                    updateNotification.assigned_agent_name !== undefined ||
                                    updateNotification.status !== undefined
          
          if (hasAssignmentChange) {
            window.dispatchEvent(new CustomEvent('assignment_change', {
              detail: {
                conversation_id: updateNotification.conversation_id,
                type: 'assignment_change',
                status: updateNotification.status,
                assigned_agent_id: updateNotification.assigned_agent_id,
                assigned_agent_name: updateNotification.assigned_agent_name,
                handoff_status: (notification as any).handoff_status,
                timestamp: notification.timestamp
              }
            }))
            
            logger.info('🔄 Dispatched assignment_change event', {
              conversation_id: updateNotification.conversation_id,
              status: updateNotification.status,
              assigned_agent_name: updateNotification.assigned_agent_name
            })
          }
        }
        break
      
      case 'agent_presence_update':
        this.eventHandlers.onAgentPresenceUpdate?.(notification)
        this.notifyListeners('agent_presence_update', notification)
        break

      case 'new_conversation_assigned':
        logger.info('🆕 New conversation assignment notification received', {
          type: notification.type,
          conversation_id: (notification as NewConversationAssignment).conversation_id,
          visitor_name: (notification as NewConversationAssignment).conversation_data.visitor_name,
          handoff_reason: (notification as NewConversationAssignment).conversation_data.handoff_reason,
          timestamp: (notification as NewConversationAssignment).timestamp
        })
        this.eventHandlers.onNewConversationAssigned?.(notification)
        this.notifyListeners('new_conversation_assigned', notification)
        
        // 🎯 Dispatch custom event for conversation list to refresh
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('newConversationAssigned', {
            detail: {
              conversationId: (notification as NewConversationAssignment).conversation_id,
              conversationData: (notification as NewConversationAssignment).conversation_data,
              timestamp: (notification as NewConversationAssignment).timestamp
            }
          }))
        }
        break

      case 'conversation_handoff':
        logger.info('🔄 Conversation handoff notification received', {
          type: notification.type,
          conversation_id: (notification as ConversationHandoff).conversation_id,
          from_type: (notification as ConversationHandoff).from_type,
          timestamp: (notification as ConversationHandoff).timestamp
        })
        this.eventHandlers.onConversationHandoff?.(notification)
        this.notifyListeners('conversation_handoff', notification)
        
        // 🎯 Dispatch custom event for conversation list to refresh
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('conversationHandoff', {
            detail: {
              conversationId: (notification as ConversationHandoff).conversation_id,
              fromType: (notification as ConversationHandoff).from_type,
              handoffData: (notification as ConversationHandoff).handoff_data,
              timestamp: (notification as ConversationHandoff).timestamp
            }
          }))
        }
        break

      case 'message_received':
        // Handle both agent and customer messages for sidebar refresh
        logger.info('📨 Message received, triggering sidebar refresh', {
          conversationId: (notification as MessageReceived).conversation_id,
          messageContent: (notification as MessageReceived).content,
          nestedContent: (notification as MessageReceived).data?.content,
          nestedMessage: (notification as MessageReceived).data?.message,
          senderType: (notification as MessageReceived).source,
          nestedRole: (notification as MessageReceived).data?.role,
          timestamp: (notification as MessageReceived).timestamp,
          fullData: notification
        })
        
        // Call the event handler
        this.eventHandlers.onMessageReceived?.(notification as MessageReceived)
        
        // Format and trigger conversation update for both agent and customer messages
        const messageData = notification as any
        
        // Extract content from nested data if needed
        const content = messageData.content || messageData.data?.content || messageData.data?.message || ""
        const source = messageData.source || messageData.data?.source || messageData.data?.role || 'unknown'
        
        if (messageData.conversation_id && content) {
          const truncatedContent = content.length > 100 ? content.substring(0, 100) + "..." : content
          const formattedMessage = source === 'user' 
            ? `Customer: ${truncatedContent}` 
            : `Agent: ${truncatedContent}`
          
          logger.info('🔄 Processing message for sidebar update', {
            conversationId: messageData.conversation_id,
            content: content,
            source: source,
            formattedMessage: formattedMessage
          })
          
          // Trigger conversation update to refresh sidebar preview
          const conversationUpdateData = {
            type: 'conversation_update',
            conversation_id: messageData.conversation_id,
            last_message: formattedMessage,
            last_message_time: messageData.timestamp,
            timestamp: messageData.timestamp
          }
          
          // Notify all conversation update listeners (WhatsApp style)
          this.eventHandlers.onConversationUpdate?.(conversationUpdateData)
          this.notifyListeners('conversation_update', conversationUpdateData)
          
          // Also dispatch as custom event for direct sidebar listening
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('conversationMessageUpdate', {
              detail: {
                conversationId: messageData.conversation_id,
                lastMessage: formattedMessage,
                lastMessageTime: messageData.timestamp,
                messageType: source === 'user' ? 'customer' : 'agent'
              }
            }))
          }
        } else {
          logger.warn('❌ Message received but missing conversation_id or content', {
            hasConversationId: !!messageData.conversation_id,
            hasContent: !!content,
            messageData: messageData
          })
        }
        
        this.notifyListeners('message_received', notification)
        break

      case 'widget_message_received':
        // Handle widget messages for sidebar refresh
        logger.info('📨 Widget message received, triggering sidebar refresh', {
          conversationId: (notification as WidgetMessageReceived).conversation_id,
          messageContent: (notification as WidgetMessageReceived).message_content,
          timestamp: (notification as WidgetMessageReceived).timestamp
        })
        
        // Call the event handler
        this.eventHandlers.onWidgetMessageReceived?.(notification as WidgetMessageReceived)
        
        // 🎯 CRITICAL: Also trigger unread count update for this conversation
        const widgetNotification = notification as any
        if (widgetNotification.unread_count) {
          this.eventHandlers.onUnreadCountUpdate?.({
            type: 'unread_count_update',
            conversation_id: widgetNotification.conversation_id,
            unread_count: widgetNotification.unread_count,
            total_unread: widgetNotification.unread_count, // Will be updated by backend
            timestamp: widgetNotification.timestamp
          })
          this.notifyListeners('unread_count_update', {
            type: 'unread_count_update',
            conversation_id: widgetNotification.conversation_id,
            unread_count: widgetNotification.unread_count,
            total_unread: widgetNotification.unread_count,
            timestamp: widgetNotification.timestamp
          })
          
          // 🔵 Dispatch unread count update as custom event for sidebar
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('unreadCountUpdate', {
              detail: {
                conversationId: widgetNotification.conversation_id,
                unreadCount: widgetNotification.unread_count,
                timestamp: widgetNotification.timestamp
              }
            }))
          }
        }
        
        // 🚨 CRITICAL FIX: Re-add the messageReceived event dispatch that was accidentally removed
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('messageReceived', {
            detail: { 
              conversationId: (notification as any).conversation_id,
              messageId: (notification as any).message_id,
              content: (notification as any).message_content,
              timestamp: (notification as any).timestamp,
              unreadCount: (notification as any).unread_count
            }
          }))
        }
        
        // 🔄 NEW: Trigger conversation update with new message preview
        const widgetMessageData = notification as any
        if (widgetMessageData.conversation_id && widgetMessageData.message_content) {
          // Format message preview similar to backend logic
          const content = widgetMessageData.message_content || ""
          const truncatedContent = content.length > 100 ? content.substring(0, 100) + "..." : content
          const formattedMessage = widgetMessageData.role === 'user' 
            ? `Customer: ${truncatedContent}` 
            : `Agent: ${truncatedContent}`
          
          // Trigger conversation update to refresh sidebar preview
          const conversationUpdateData = {
            type: 'conversation_update',
            conversation_id: widgetMessageData.conversation_id,
            last_message: formattedMessage,
            last_message_time: widgetMessageData.timestamp,
            unread_count: widgetMessageData.unread_count || 0,
            timestamp: widgetMessageData.timestamp
          }
          
          // Notify all conversation update listeners (WhatsApp style)
          this.eventHandlers.onConversationUpdate?.(conversationUpdateData)
          this.notifyListeners('conversation_update', conversationUpdateData)
          
          // Also dispatch as custom event for direct sidebar listening
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('conversationMessageUpdate', {
              detail: {
                conversationId: widgetMessageData.conversation_id,
                lastMessage: formattedMessage,
                lastMessageTime: widgetMessageData.timestamp,
                unreadCount: widgetMessageData.unread_count || 0,
                messageType: 'customer'
              }
            }))
          }
        }
        
        this.notifyListeners('widget_message_received', notification)
        break

      case 'customer_ended_chat':
        this.eventHandlers.onCustomerEndedChat?.(notification)
        this.notifyListeners('customer_ended_chat', notification)
        
        // Dispatch custom event for conversation list updates
        if (typeof window !== 'undefined') {
          const customerEndData = notification as any
          window.dispatchEvent(new CustomEvent('customerEndedChat', {
            detail: {
              conversationId: customerEndData.conversation_id,
              sessionId: customerEndData.data?.session_id,
              endedAt: customerEndData.data?.ended_at,
              sessionDuration: customerEndData.data?.session_duration,
              conversationStatus: customerEndData.data?.conversation_status,
              autoResolved: customerEndData.data?.auto_resolved,
              visitorName: customerEndData.data?.visitor_name,
              timestamp: customerEndData.timestamp || new Date().toISOString()
            }
          }))
        }
        break
    }

    // Notify all listeners
    this.notifyListeners('*', notification)
  }

  /**
   * Notify registered listeners
   */
  private notifyListeners(eventType: string, data: any): void {
    const listeners = this.listeners.get(eventType)
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data)
        } catch (error) {
          logger.error('Error in notification listener', { error, eventType })
        }
      })
    }
  }

  /**
   * Set event handlers
   */
  setEventHandlers(handlers: RealTimeEventHandlers): void {
    this.eventHandlers = { ...this.eventHandlers, ...handlers }
  }

  /**
   * Add event listener
   */
  addEventListener(eventType: string, callback: (data: any) => void): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set())
    }
    
    this.listeners.get(eventType)!.add(callback)

    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(eventType)
      if (listeners) {
        listeners.delete(callback)
        if (listeners.size === 0) {
          this.listeners.delete(eventType)
        }
      }
    }
  }

  /**
   * Check connection status
   */
  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }

  /**
   * Get connection state
   */
  get connectionState(): string {
    if (!this.ws) return 'disconnected'
    
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING: return 'connecting'
      case WebSocket.OPEN: return 'connected'
      case WebSocket.CLOSING: return 'closing'
      case WebSocket.CLOSED: return 'disconnected'
      default: return 'unknown'
    }
  }
}

// Export singleton instance
export const realTimeNotifications = new RealTimeNotificationService()

// Auto-connect when module is imported
if (typeof window !== 'undefined') {
  // Connect after a short delay to ensure auth is ready
  setTimeout(() => {
    realTimeNotifications.connect()
  }, 1000)
}